import { Component, inject } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastService } from '@peca/commons';
import { AutocompleteItem, BreadcrumbItem } from '@peca/design-system';
import { BranchSuggestion } from '../../../../sellers/models/branch-suggestion.model';
import { SellerService } from '../../../../sellers/services/seller.service';
import { AnyMarketExtractionFilter, SellerBranch } from '../../../models/anymarket-publication.model';
import { AnyMarketPublicationService } from '../../../services/anymarket-publication.service';
import {
  MultiSelectAutocompleteItem,
  MultiSelectAutocompleteItemSource,
} from 'libs/design-system/src/lib/components/multi-select-autocomplete/multi-select-autocomplete.model';

@Component({
  templateUrl: './anymarket-settings.component.html',
  styleUrls: ['./anymarket-settings.component.scss'],
  standalone: false,
})
export class AnyMarketSettingsComponent {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly toast = inject(ToastService);
  private readonly sellerService = inject(SellerService);
  private readonly publicationService = inject(AnyMarketPublicationService);

  readonly breadcrumbItems: BreadcrumbItem[] = [
    { label: 'Operações', path: '/operacoes' },
    { label: 'Anymarket', path: '/operacoes/anymarket' },
    { label: 'Publicações', path: '/operacoes/anymarket/publicacoes' },
    { label: 'Configurações' },
  ];

  isSubmitting = false;
  isBrandBlacklist = true;
  isProductGroupBlacklist = true;
  isBranchBlacklist = true;

  readonly form = new FormGroup({
    brand: new FormControl<MultiSelectAutocompleteItem[]>([]),
    productGroup: new FormControl<MultiSelectAutocompleteItem[]>([]),
    branchs: new FormControl<MultiSelectAutocompleteItem<BranchSuggestion>[]>([]),
    deliveryTime: new FormControl<string | number | null>(null),
  });

  brandSource = new MultiSelectAutocompleteItemSource();
  productGroupSource = new MultiSelectAutocompleteItemSource();
  branchSource = new MultiSelectAutocompleteItemSource<BranchSuggestion>();

  get brandCount(): number {
    return this.form.getRawValue().brand?.length || 0;
  }

  get productGroupCount(): number {
    return this.form.getRawValue().productGroup?.length || 0;
  }

  get branchCount(): number {
    return this.form.getRawValue().branchs?.length || 0;
  }

  get totalSelectedValues(): number {
    return this.brandCount + this.productGroupCount + this.branchCount;
  }

  get hasInvalidWhitelistFilter(): boolean {
    if (!this.isBrandBlacklist && this.brandCount === 0) return true;
    if (!this.isProductGroupBlacklist && this.productGroupCount === 0) return true;
    if (!this.isBranchBlacklist && this.branchCount === 0) return true;
    return false;
  }

  get canSubmit(): boolean {
    if (this.isSubmitting) return false;
    if (this.hasInvalidWhitelistFilter) return false;
    return true;
  }

  onBrandModeChange(value: boolean) {
    this.isBrandBlacklist = value;
  }

  onProductGroupModeChange(value: boolean) {
    this.isProductGroupBlacklist = value;
  }

  onBranchModeChange(value: boolean) {
    this.isBranchBlacklist = value;
  }

  onBrandAutocomplete(search: string) {
    if (search.trim().length < 2) {
      this.brandSource = new MultiSelectAutocompleteItemSource();
      return;
    }

    this.brandSource = new MultiSelectAutocompleteItemSource([], true);
    this.publicationService.suggestBrands(search).subscribe({
      next: (items) => (this.brandSource = new MultiSelectAutocompleteItemSource(this.toMultiSelectItems(items))),
      error: () => (this.brandSource = new MultiSelectAutocompleteItemSource([], false, true)),
    });
  }

  onProductGroupAutocomplete(search: string) {
    if (search.trim().length < 3) {
      this.productGroupSource = new MultiSelectAutocompleteItemSource();
      return;
    }

    this.productGroupSource = new MultiSelectAutocompleteItemSource([], true);
    this.publicationService.suggestProductGroups(search).subscribe({
      next: (items) => (this.productGroupSource = new MultiSelectAutocompleteItemSource(this.toMultiSelectItems(items))),
      error: () => (this.productGroupSource = new MultiSelectAutocompleteItemSource([], false, true)),
    });
  }

  onBranchAutocomplete(search: string) {
    if (search.trim().length < 3) {
      this.branchSource = new MultiSelectAutocompleteItemSource();
      return;
    }

    this.branchSource = new MultiSelectAutocompleteItemSource([], true);
    this.sellerService.suggestionBranches(search).subscribe({
      next: (branches) => (this.branchSource = new MultiSelectAutocompleteItemSource(branches.map((branch) => this.toBranchItem(branch)))),
      error: () => (this.branchSource = new MultiSelectAutocompleteItemSource([], false, true)),
    });
  }

  private toMultiSelectItems(items: AutocompleteItem[]): MultiSelectAutocompleteItem[] {
    return items.map((item) => ({
      id: String(item.id),
      label: item.label,
      description: item.description,
      selected: false,
    }));
  }

  private toBranchItem(branch: BranchSuggestion): MultiSelectAutocompleteItem<BranchSuggestion> {
    return {
      id: String(branch.id),
      label: `${branch.tradingName || branch.sellerName} - ${branch.commercialName}`,
      description: branch.externalReference,
      payload: branch,
      selected: false,
    };
  }

  onSubmit() {
    if (!this.canSubmit) return;

    this.isSubmitting = true;
    this.publicationService.create(this.toPublicationFilters()).subscribe({
      next: () => {
        this.toast.success('Publicação agendada com sucesso.');
        this.router.navigate(['../publicacoes'], { relativeTo: this.route });
      },
      error: () => {
        this.isSubmitting = false;
        this.toast.failure('Não foi possível agendar a publicação.');
      },
    });
  }

  private toPublicationFilters(): AnyMarketExtractionFilter {
    const value = this.form.getRawValue();
    const deliveryTimeStr = value.deliveryTime !== null && value.deliveryTime !== undefined ? String(value.deliveryTime).trim() : '';
    const deliveryTime = deliveryTimeStr !== '' ? Number(deliveryTimeStr) : null;

    const brandValues = (value.brand || []).map(({ id }) => String(id));
    const groupValues = (value.productGroup || []).map(({ id }) => String(id));
    const branchValues: SellerBranch[] = (value.branchs || []).map((item) => {
      const suggestion = item.payload;
      return {
        seller: suggestion?.sellerName || '',
        branch: suggestion?.externalReference || String(item.id),
      };
    });

    return {
      brandsId: {
        isBlacklist: this.isBrandBlacklist,
        values: brandValues,
      },
      productGroupsId: {
        isBlacklist: this.isProductGroupBlacklist,
        values: groupValues,
      },
      branches: {
        isBlacklist: this.isBranchBlacklist,
        values: branchValues,
      },
      deliveryTimeInMinutes: deliveryTime,
    };
  }

  navigateBack() {
    this.router.navigate(['../publicacoes'], { relativeTo: this.route });
  }
}
